// src/views/UserViews/insights/components/DeviceStatusDonut.tsx
//
// PieChart over the exclusive is_recently_active/is_offline_over_24h partition (Rule 5).
// This is deliberately NOT built from the four FleetStatusSection numbers - those are
// independent, overlapping filters (a device can be low battery AND recently active), so
// summing them would double-count. Low battery has no wedge here; it stays a standalone
// card in M4's FleetStatusSection.

import { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { StringKeys } from '@/resources/strings/StringKeys';
import COLORS from '@/resources/style/Themes';
import type { InsightsSectionState } from '@/hooks/insights/useInsightsData';
import {
  EXCLUSIVE_DEVICE_STATUS_SLICE_LABELS,
  NOT_SEEN_OVER_24H_SLICE_LABEL,
  RECENTLY_ACTIVE_SLICE_LABEL,
  SEEN_LAST_24H_SLICE_LABEL,
  type DeviceStatusSlice,
} from '@/models/Insights/InsightsTypes';
import SectionCard from './SectionCard';

/** Fixed square size (both `width` and `height`, explicit on `PieChart` below) for the
 * donut's own drawing area, independent of the card's height. Kept as an explicit, fixed
 * value rather than derived from the card's height - see this file's `ComplianceTrendChart`
 * sibling comment on `CHART_HEIGHT` for why letting `PieChart` size itself from a page
 * ancestor with no definite height is unsafe.
 *
 * The legend now stacks *beneath* the donut (see the layout below) rather than beside it, so
 * this is no longer constrained by the ~360px column's leftover width next to a legend - it is
 * a fixed value sized to the ring's own visual proportions (see `DONUT_INNER_RADIUS`). This
 * card's natural height (this donut plus its caption and three stacked legend rows) is what now
 * lands close to `ComplianceTrendChart`'s `CHART_HEIGHT`, not the other way around - see that
 * constant's comment. */
const DONUT_SIZE = 190;
/** `PieChart`'s own `outerRadius` default (unset here) is derived from its internal margin,
 * making the actual ring thickness (`outerRadius - innerRadius`) implicit. Both radii are set
 * explicitly below instead: `DONUT_OUTER_RADIUS` sizes the ring to fill most of `DONUT_SIZE`
 * (see the `margin` prop on `PieChart`, which is shrunk to near-zero so the ring isn't
 * squeezed smaller by margin-reserved space it no longer needs), and `DONUT_RING_THICKNESS`
 * is the actual visual thickness, set directly rather than back-calculated from a ratio. */
const DONUT_OUTER_RADIUS = 92;
const DONUT_RING_THICKNESS = 32;
/** Purely a visual-proportion choice now that the caption lives outside the chart's SVG (see
 * `DonutCenterLabel` below) - the hole no longer has to clear any rendered text, so this is
 * just `DONUT_OUTER_RADIUS - DONUT_RING_THICKNESS`, sized to keep the ring looking like a
 * donut rather than a solid disc or an overly thin band. */
const DONUT_INNER_RADIUS = DONUT_OUTER_RADIUS - DONUT_RING_THICKNESS;

/** Spacing constants for the legend block beneath the donut, factored out so the loading
 * `Skeleton`'s `minHeight` (below) can be derived from the same numbers the real legend
 * markup uses, rather than a separately-guessed magic number that could drift from it. Any
 * new spacing added to the legend block (e.g. `LEGEND_BOTTOM_SPACING_PX`) - or, below, to the
 * caption block sitting above it - must be folded into `LEGEND_BLOCK_HEIGHT_PX` /
 * `DONUT_CARD_MIN_HEIGHT` too, or the loaded card's real height would drift from the
 * `Skeleton`'s and the card would visibly resize once loading finishes. */
const LEGEND_TOP_MARGIN_PX = 16; // matches the list's `mt: 2`
const LEGEND_ROW_HEIGHT_PX = 20; // approx. line height of one `body2` legend row
const LEGEND_ROW_GAP_PX = 12; // matches the list's `gap: 1.5`
/** Breathing room below the last legend row so it doesn't sit flush against the card's own
 * bottom padding - matches `LEGEND_TOP_MARGIN_PX` for a symmetrical top/bottom feel. */
const LEGEND_BOTTOM_SPACING_PX = 16;
const LEGEND_ROW_COUNT = EXCLUSIVE_DEVICE_STATUS_SLICE_LABELS.length;
const LEGEND_BLOCK_HEIGHT_PX =
  LEGEND_TOP_MARGIN_PX +
  LEGEND_ROW_COUNT * LEGEND_ROW_HEIGHT_PX +
  (LEGEND_ROW_COUNT - 1) * LEGEND_ROW_GAP_PX +
  LEGEND_BOTTOM_SPACING_PX;
/** The caption now renders as a normal `Typography` beneath the `PieChart` (see
 * `DonutCenterLabel`'s comment) rather than as an SVG `<text>` inside it, so - unlike its old
 * home, which added zero real DOM layout height - it now occupies a real line in the document
 * flow between the donut and the legend list. `CAPTION_LINE_HEIGHT_PX` approximates MUI's
 * `caption` variant line height (12px font * ~1.66 line-height); `CAPTION_MARGIN_TOP_PX` /
 * `CAPTION_MARGIN_BOTTOM_PX` match the `sx` margins applied to that Typography below. All
 * three must stay in sync with the real markup or this derivation drifts from the actual
 * rendered height. */
const CAPTION_LINE_HEIGHT_PX = 20;
const CAPTION_MARGIN_TOP_PX = 6;
const CAPTION_MARGIN_BOTTOM_PX = 6;
const CAPTION_BLOCK_HEIGHT_PX =
  CAPTION_MARGIN_TOP_PX + CAPTION_LINE_HEIGHT_PX + CAPTION_MARGIN_BOTTOM_PX;
/** The donut card's natural content height once loaded (donut, plus its caption, plus its
 * stacked legend) - used only to size the loading `Skeleton` below, so switching between
 * loading and loaded states doesn't itself change the card's height. */
const DONUT_CARD_MIN_HEIGHT = DONUT_SIZE + CAPTION_BLOCK_HEIGHT_PX + LEGEND_BLOCK_HEIGHT_PX;
/** Fixed minimum width for the percent/count segment of each legend row (e.g. "100% · 999"),
 * right-aligned, so that segment forms a clean column down the list regardless of how long
 * each row's label is - see the legend markup below. */
const LEGEND_STAT_MIN_WIDTH_PX = 64;

/** One color per canonical slice, in the same order as `EXCLUSIVE_DEVICE_STATUS_SLICE_LABELS`
 * / `partitionDeviceStatusSlices`'s output - shared between the pie series and the custom
 * legend below so a swatch can never drift from its wedge. */
const SLICE_COLORS = [COLORS.SAFEGUARD_BLUE, '#6B8CAE', COLORS.SAFEGUARD_ORANGE] as const;

/** Maps each canonical slice-label constant (the data-matching keys `partitionDeviceStatusSlices`
 * buckets rows by - see that function's comment) to a short, translated legend `StringKey`. The
 * canonical constants stay wired to `slice.id`/`slice.label` for partitioning and coloring; this
 * map is consulted only when rendering the legend text, so a display-string change here can never
 * affect which bucket a row lands in. Keyed by the raw English constants (not translation keys)
 * because those constants - not any locale's text - are what `InsightsDAO.deviceStatusSliceLabel`
 * actually emits and what `slice.id` is set to. */
const SLICE_LABEL_DISPLAY_KEYS: Record<string, string> = {
  [RECENTLY_ACTIVE_SLICE_LABEL]: StringKeys.INSIGHTS_DEVICE_STATUS_LABEL_RECENTLY_ACTIVE,
  [SEEN_LAST_24H_SLICE_LABEL]: StringKeys.INSIGHTS_DEVICE_STATUS_LABEL_SEEN_24H,
  [NOT_SEEN_OVER_24H_SLICE_LABEL]: StringKeys.INSIGHTS_DEVICE_STATUS_LABEL_NOT_SEEN_24H,
};

export type DonutSlice = { id: string; label: string; value: number };
export type DonutPartition = {
  slices: DonutSlice[];
  total: number;
  anomalies: DeviceStatusSlice[];
};

/**
 * Folds `getDeviceStatusBreakdown` rows into the exclusive three-slice partition. Any row
 * whose label is not one of the three canonical slices - chiefly the impossible
 * is_recently_active=1/is_offline_over_24h=1 cell, which `InsightsDAO`'s own defensive
 * fallback maps to "Unknown" rather than throwing - is excluded from both the slices and
 * the displayed total, and is surfaced via `console.warn` rather than being silently folded
 * into one of the three real buckets (which would corrupt an otherwise-exclusive count).
 */
export function partitionDeviceStatusSlices(rows: DeviceStatusSlice[]): DonutPartition {
  const countByLabel = new Map<string, number>(
    EXCLUSIVE_DEVICE_STATUS_SLICE_LABELS.map((label) => [label, 0]),
  );
  const anomalies: DeviceStatusSlice[] = [];

  for (const row of rows) {
    if (countByLabel.has(row.label)) {
      countByLabel.set(row.label, (countByLabel.get(row.label) ?? 0) + row.count);
    } else {
      anomalies.push(row);
    }
  }

  if (anomalies.length > 0) {
    // Surface the data-quality issue rather than silently dropping rows outside the
    // exclusive partition (Task 5.2) - this project's lint config does not flag `console`.
    console.warn(
      'DeviceStatusDonut: device-status rows fell outside the exclusive 3-slice partition ' +
        '(most likely the impossible is_recently_active=1/is_offline_over_24h=1 cell). ' +
        'Excluded from the donut rather than folded into a slice:',
      anomalies,
    );
  }

  const slices = EXCLUSIVE_DEVICE_STATUS_SLICE_LABELS.map((label) => ({
    id: label,
    label,
    value: countByLabel.get(label) ?? 0,
  }));

  return {
    slices,
    total: slices.reduce((sum, slice) => sum + slice.value, 0),
    anomalies,
  };
}

/** Typography does not scale with the drawing area (see `DONUT_SIZE`'s comment) - this is a
 * fixed pixel size, not a ratio of `DONUT_SIZE`. */
const VALUE_FONT_SIZE = 26;
/** A single centered line needs no vertical offset - `centerY` (below) already lands on the
 * ring's true center. This only existed before to push the value line up and make room for a
 * second, stacked caption line beneath it inside the same SVG; now that the caption renders
 * as its own `Typography` outside the chart (see the caption markup below), there is nothing
 * left to offset against. */
const VALUE_OFFSET_Y = 0;

type DonutCenterLabelProps = { total: number };

/** Standard MUI X Charts "pie chart with center label" pattern: a plain SVG `<text>`
 * positioned via `useDrawingArea`, rendered as a child of `PieChart` (children render
 * inside the chart's own SVG surface). Renders only the total value - no caption - so the
 * ring's hole never has to clear rendered text width; the caption lives in the surrounding
 * layout instead (see the caption `Typography` in the markup below). */
function DonutCenterLabel({ total }: DonutCenterLabelProps) {
  const { width, height, left, top } = useDrawingArea();
  const centerX = left + width / 2;
  const centerY = top + height / 2;

  return (
    <text
      data-testid="device-status-donut-total-value"
      x={centerX}
      y={centerY + VALUE_OFFSET_Y}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={VALUE_FONT_SIZE}
      fontWeight={600}
    >
      {total}
    </text>
  );
}

type DeviceStatusDonutProps = {
  status: InsightsSectionState<DeviceStatusSlice[]>;
};

export default function DeviceStatusDonut({ status }: DeviceStatusDonutProps) {
  const { t } = useTranslation();
  const { data, loading, error } = status;

  const partition = useMemo(() => partitionDeviceStatusSlices(data ?? []), [data]);
  // Attaches a color to each slice for the pie series without touching
  // `partitionDeviceStatusSlices`'s own output - the same array, in the same order, feeds
  // both this and the legend below so a swatch can never point at the wrong wedge.
  const coloredSlices = useMemo(
    () => partition.slices.map((slice, index) => ({ ...slice, color: SLICE_COLORS[index] })),
    [partition.slices],
  );

  return (
    <SectionCard
      title={t(StringKeys.INSIGHTS_DEVICE_STATUS_SECTION_TITLE)}
      subtitle={t(StringKeys.INSIGHTS_DEVICE_STATUS_SECTION_SUBTITLE)}
    >
      {loading && (
        <Skeleton
          variant="rectangular"
          sx={{ flex: 1, minHeight: DONUT_CARD_MIN_HEIGHT }}
          data-testid="device-status-donut-loading"
        />
      )}

      {!loading && error && (
        <Typography variant="body2" color="error" data-testid="device-status-donut-error">
          {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
        </Typography>
      )}

      {!loading && !error && data && (
        // `flex: 1` fills whatever height SectionCard's flex column hands this card (see
        // Insights.tsx's grid); `justifyContent: 'center'` centers the donut + legend column
        // within that space. This is no longer a near-no-op now that the two cards' natural
        // heights are close but not identical (see `ComplianceTrendChart`'s `CHART_HEIGHT`
        // comment) - the grid row's `stretch` alignment can still hand this card a few extra
        // px over its own content height, and this is what absorbs that real leftover
        // instead of letting it collect as dead space.
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* Centers the fixed-size donut horizontally now that the legend sits beneath it
           * (full-width) rather than beside it - without this the donut would hug the left
           * edge like any other flex-column child. */}
          <Box sx={{ alignSelf: 'center' }}>
            <PieChart
              width={DONUT_SIZE}
              height={DONUT_SIZE}
              hideLegend
              // Near-zero rather than `PieChart`'s default 20px-per-side margin, which used to
              // reserve blank space between the ring and the SVG's own edges (see
              // `DONUT_OUTER_RADIUS`'s comment) - the card's title-to-ring and ring-to-legend
              // gaps are now controlled by the surrounding layout, not by unused chart margin.
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              series={[
                {
                  data: coloredSlices,
                  innerRadius: DONUT_INNER_RADIUS,
                  outerRadius: DONUT_OUTER_RADIUS,
                  paddingAngle: 1,
                  cornerRadius: 2,
                },
              ]}
            >
              <DonutCenterLabel total={partition.total} />
            </PieChart>
          </Box>

          {/* Rendered as a normal DOM `Typography`, not an SVG `<text>` inside `PieChart` (see
           * `DonutCenterLabel`'s comment) - this is real layout height between the donut and
           * the legend, folded into `CAPTION_BLOCK_HEIGHT_PX` / `DONUT_CARD_MIN_HEIGHT` above
           * so the loading `Skeleton` doesn't drift from this card's real rendered height. */}
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="device-status-donut-total-caption"
            sx={{
              textAlign: 'center',
              mt: `${CAPTION_MARGIN_TOP_PX}px`,
              mb: `${CAPTION_MARGIN_BOTTOM_PX}px`,
            }}
          >
            {t(StringKeys.INSIGHTS_DEVICE_STATUS_DONUT_TOTAL_CAPTION)}
          </Typography>

          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              mt: 2,
              mb: `${LEGEND_BOTTOM_SPACING_PX}px`,
              p: 0,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {partition.slices.map((slice, index) => {
              const percent =
                partition.total > 0 ? Math.round((slice.value / partition.total) * 100) : 0;
              // Falls back to the raw canonical label rather than an untranslated key or
              // `undefined` if a slice id somehow isn't one of the three mapped constants -
              // see `SLICE_LABEL_DISPLAY_KEYS`'s comment.
              const displayLabelKey = SLICE_LABEL_DISPLAY_KEYS[slice.id];
              const displayLabel = displayLabelKey ? t(displayLabelKey) : slice.label;
              return (
                <Box
                  component="li"
                  key={slice.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: SLICE_COLORS[index],
                      flexShrink: 0,
                    }}
                  />
                  {/* `minWidth: 0` lets this shrink below its own text's intrinsic width so
                   * the row never grows the card wider than its container; `noWrap` (MUI's
                   * single-line-with-ellipsis prop) favors truncating a single line over
                   * wrapping to a second one, so "one line per row" holds even in an edge
                   * case (a very narrow card combined with an unusually long label). */}
                  <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {displayLabel}
                  </Typography>
                  {/* Fixed `minWidth`, right-aligned: at full list width (no longer squeezed
                   * beside a donut) each label already fits on one line, so this segment is
                   * what needs the alignment help - a fixed column here keeps the percent/
                   * count text from ragging against the card's right edge row to row. */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: LEGEND_STAT_MIN_WIDTH_PX, textAlign: 'right', flexShrink: 0 }}
                  >
                    {t(StringKeys.INSIGHTS_DEVICE_STATUS_LEGEND_ROW, {
                      percent,
                      deviceCount: slice.value,
                    })}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </SectionCard>
  );
}
